import { Component, Suspense, lazy, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import AuthLayout from '@/layouts/AuthLayout'
import MainLayout from '@/layouts/MainLayout'

const Login = lazy(() => import('@/pages/Login'))
const Register = lazy(() => import('@/pages/Register'))
const Forgot = lazy(() => import('@/pages/Forgot'))
const Reset = lazy(() => import('@/pages/Reset'))
const Verify = lazy(() => import('@/pages/Verify'))
const Products = lazy(() => import('@/pages/Products'))
const Orders = lazy(() => import('@/pages/Orders'))
const OrderDetail = lazy(() => import('@/pages/OrderDetail'))
const Licenses = lazy(() => import('@/pages/Licenses'))
const LicenseDetail = lazy(() => import('@/pages/LicenseDetail'))
const Profile = lazy(() => import('@/pages/Profile'))
const BuyPage = lazy(() => import('@/pages/BuyPage'))

// session lives in an HttpOnly cookie; probe /me instead of a storage key
function RequireAuth() {
  const profile = useAuthStore((s) => s.profile)
  const fetchMe = useAuthStore((s) => s.fetchMe)
  const location = useLocation()
  const [state, setState] = useState<'checking' | 'ok' | 'fail'>(profile ? 'ok' : 'checking')

  useEffect(() => {
    if (profile) {
      setState('ok')
      return
    }
    setState('checking')
    let alive = true
    fetchMe()
      .then(() => alive && setState('ok'))
      .catch(() => alive && setState('fail'))
    return () => {
      alive = false
    }
  }, [profile, fetchMe])

  if (state === 'ok') return <Outlet />
  if (state === 'fail') {
    const target = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`
    return <Navigate to={target} replace />
  }
  return null
}

// 懒加载 chunk 失效（部署后文件名哈希变化、网络瞬断）会导致路由视图空白：
// 整页刷新一次即可拿到新入口与 chunk。
const CHUNK_RELOAD_FLAG = 'licensor-portal-chunk-reloaded'

function isChunkError(error: unknown): boolean {
  const msg = String((error as Error)?.message ?? error)
  return /dynamically imported module|Importing a module script failed|Loading chunk|Failed to fetch dynamically/i.test(msg)
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    ;(window as any).__licensorError = String((error as Error)?.stack || error)
    if (isChunkError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
        location.reload()
      }
      return
    }
    // 渲染/生命周期错误不再静默：留痕便于定位（避免只看到空白页）
    console.error('[licensor] unhandled error:', info.componentStack, error)
  }

  render() {
    if (this.state.error) {
      if (isChunkError(this.state.error)) return null
      // 可见的崩溃回退：比整页空白更可诊断
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0d0e', color: '#f8fafc', fontFamily: 'monospace', padding: 24 }}>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{String(this.state.error?.message || this.state.error)}</pre>
        </div>
      )
    }
    return this.props.children
  }
}

export function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot" element={<Forgot />} />
            <Route path="reset" element={<Reset />} />
            <Route path="verify" element={<Verify />} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route element={<MainLayout />}>
              <Route path="products" element={<Products />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:code" element={<OrderDetail />} />
              <Route path="licenses" element={<Licenses />} />
              <Route path="licenses/:id" element={<LicenseDetail />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Route>
          <Route path="/" element={<Navigate to="/products" replace />} />
          {/* 公开购买落地页：官网 Buy 入口直达，未登录时引导登录后回到本页 */}
          <Route path="buy/:code" element={<BuyPage />} />
          {/* 未匹配路径兜底（含 chunk 失效后的陈旧链接），避免整页空白 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
